import type { TopUpSettings } from "@pax/api-contract/payments/v1alpha1";
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  panelCard,
} from "@pax/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CreditCard } from "lucide-react";
import { useState } from "react";
import { api, reachable, requestErrorMessage } from "@/lib/api";
import { paymentMethodLabel } from "../format";
import { mySubscriptionQuery, offersQuery, topUpSettingsQuery } from "../queries";

/** The payment method Pax pays with from this dialog, and the way to stop using it. */
export function SavedPaymentMethodSection({
  settings,
  disabled,
}: {
  settings: TopUpSettings;
  disabled: boolean;
}) {
  const [confirming, setConfirming] = useState(false);

  if (!settings.paymentMethod) return null;
  const label = paymentMethodLabel(settings.paymentMethod);

  return (
    <div className={cn(panelCard, "flex flex-wrap items-center gap-x-3 gap-y-2 p-3 text-sm")}>
      <CreditCard aria-hidden className="size-4 shrink-0 text-muted-foreground" />
      <p className="min-w-0 flex-1 basis-[min(100%,12rem)]">
        <span className="font-medium">{label}</span>
        <span className="text-muted-foreground"> is saved for purchases.</span>
      </p>
      <Button variant="outline" size="sm" disabled={disabled} onClick={() => setConfirming(true)}>
        Remove payment method
      </Button>
      <RemovePaymentMethodDialog
        settings={settings}
        open={confirming}
        onOpenChange={setConfirming}
      />
    </div>
  );
}

/** Asks before Pax forgets the saved payment method, and says what still renews on it. */
export function RemovePaymentMethodDialog({
  settings,
  open,
  onOpenChange,
}: {
  settings: TopUpSettings;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const subscriptions = useQuery({ ...mySubscriptionQuery, enabled: open });
  const offers = useQuery({ ...offersQuery, enabled: open });
  const remove = useMutation({
    mutationFn: () => reachable(api.payments.savedPaymentMethod.delete()),
    onSuccess: updated => {
      queryClient.setQueryData(topUpSettingsQuery.queryKey, updated);
      onOpenChange(false);
    },
  });

  const close = () => {
    remove.reset();
    onOpenChange(false);
  };
  const label = settings.paymentMethod ? paymentMethodLabel(settings.paymentMethod) : "";
  const currentPlan = subscriptions.data?.plan.current;
  const plan = currentPlan?.provider === "stripe" ? currentPlan : null;
  const planName = plan
    ? (offers.data?.find(offer => offer.offerId === plan.offerId)?.name ?? "monthly")
    : null;

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!next && !remove.isPending) close();
      }}
    >
      <DialogContent nestedBackdrop>
        <DialogHeader>
          <DialogTitle>Remove {label}?</DialogTitle>
          <DialogDescription>
            Pax won't use this payment method for one-off buys or automatic top-up.
            {planName &&
              ` Your ${planName} plan still renews on this payment method each month. To stop it, cancel it from billing.`}
          </DialogDescription>
        </DialogHeader>
        {remove.error && (
          <p
            role="alert"
            className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {requestErrorMessage(remove.error)}
          </p>
        )}
        <DialogFooter className="flex-row flex-wrap justify-end">
          <Button type="button" variant="outline" disabled={remove.isPending} onClick={close}>
            Keep payment method
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={remove.isPending}
            onClick={() => remove.mutate()}
          >
            {remove.isPending ? "Removing…" : "Remove payment method"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
